import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue';

/**
 * Single source of truth for "are we on a small (phone/tablet) screen".
 *
 * Uses `matchMedia('(max-width: 768px)')` so it reacts instantly to viewport
 * changes (rotate, resize, devtools) without a resize-throttle. The listener is
 * cleaned up on unmount.
 *
 * Mirrors the breakpoint used by the global mobile stylesheet
 * (`styles/mobile.css`) and the responsive DIALOG_WIDTH helper so the whole app
 * agrees on what "mobile" means.
 */
const MOBILE_QUERY = '(max-width: 768px)';

export function useIsMobile(): Ref<boolean> {
  const mql =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(MOBILE_QUERY)
      : null;

  const isMobile = ref(mql ? mql.matches : false);

  function update(e: MediaQueryListEvent | MediaQueryList) {
    isMobile.value = e.matches;
  }

  onMounted(() => {
    if (!mql) return;
    // Safari <14 only supports addListener; modern browsers use addEventListener.
    if (mql.addEventListener) mql.addEventListener('change', update);
    else mql.addListener(update);
    // Re-sync in case the viewport changed between setup and mount.
    isMobile.value = mql.matches;
  });

  onBeforeUnmount(() => {
    if (!mql) return;
    if (mql.removeEventListener) mql.removeEventListener('change', update);
    else mql.removeListener(update);
  });

  return isMobile;
}
